import Marionette from "backbone.marionette";
import Radio from "backbone.radio";

import DRGStatus from "../../../config/drg-status";

import Chart from "../../../models/chart-reviews/chart-form";
import TabCollection from "../../../collections/tabs";
import GenericModel from "../../../models/generic";
import EditView from "./edit";
import AuditHistory from "./audit-history";
import FileHistory from "./file-history";
import Held from "./held";

// setup data related imports
import DRGModel from "../../../models/chart-reviews/drg-deletes-edit";
import MemberEligibilityCollection from "../../../collections/chart-reviews/member-eligibility";
import StringKeyValueCollection from "../../../collections/key-string-value";
import NoteCollection from "../../../collections/notes";

import { createMemberCategories } from "../../../utils/chart-reviews/member-categories";

// common templates and environment
import templates from "../../../utils/templates";
import environment from "../../../common/environment";

const channel = Radio.channel("global");

const DEFAULTS = {
  className: "row",
  template: templates.find("layout", "generic_tabbed_view"),
};

// tab configuration
const VIEW_CONFIG = [
  { id: "drg-edit", label: "Info &amp; Status", active: true, view: EditView },
  {
    id: "drg-audit-history",
    label: "Audit History",
    active: false,
    view: AuditHistory,
  },
  {
    id: "drg-file-history",
    label: "File History",
    active: false,
    view: FileHistory,
  },
  { id: "drg-held", label: "Held", active: false, view: Held },
];

class View extends Marionette.LayoutView {
  constructor(options = {}) {
    _.defaults(options, DEFAULTS);
    super(options);
  }

  events() {
    return {
      "shown.bs.tab": "_onTabShown",
    };
  }

  childEvents() {
    return {
      "modal:close": "_hide",
    };
  }

  initialize() {
    // create tab collection
    this.collection = new TabCollection(VIEW_CONFIG);

    // create model to use when persisting data to API
    const DRGAPIModel = DRGModel.extend({
      urlRoot: this.model.urlRoot,
    });
    this.apiModel = new DRGAPIModel();

    // copy original model to UI Model be used by children views
    this.uiModel = this.model || null;

    // chart
    this.chartModel = new Chart();

    // reset passed in model to be used by layout view
    this.model = new GenericModel({
      tabs: VIEW_CONFIG,
    });

    // initialize setup data
    this.setupData = {};
  }

  onBeforeRender() {
    // add regions to layout
    this.collection.each((tab) => {
      this.addRegion(tab.getId(), `#tab-${tab.id}`);
    });
  }

  // retrieve setup data, and fetch DRG details on success
  onBeforeShow() {
    $.when(
      $.ajax(environment.getEndpoint("chart-reviews:drg-aux-types")),
      $.ajax(environment.getEndpoint("chart-reviews:places-of-service")),
      $.ajax(
        environment.getEndpoint("chart-reviews:supplemental-reason-codes")
      ),
      $.ajax(environment.getEndpoint("chart-reviews:drg-deletes-action-types"))
    ).then(
      // on success
      (auxTypes, placesOfService, supplementalReasonCodes, actions) => {
        this.setupData = {
          statusList: new StringKeyValueCollection(DRGStatus),
          auxTypes: new StringKeyValueCollection(auxTypes[0]),
          placesOfService: new StringKeyValueCollection(placesOfService[0]),
          supplementalReasonCodes: new StringKeyValueCollection(
            supplementalReasonCodes[0]
          ),
          actions: new StringKeyValueCollection(actions[0]),
        };

        // fetch DRG
        this.uiModel.promise = this.uiModel.fetch();
        this.uiModel.promise.done(this._onDRGModelLoaded.bind(this));
        this.uiModel.promise.fail(this._onDRGModelLoadFailure.bind(this));
      },
      // on failure
      () => {
        // close this modal
        this._hide();

        // show error
        channel.trigger("status:show", {
          autoDismiss: false,
          type: "danger",
          message:
            "We were unable to retrieve all the data necessary to display the Diag Review Group. Please try again later.",
        });
      }
    );
  }

  // try to avoid async waterfalls like this one...
  _onDRGModelLoaded(model) {
    // set original DRG data into model
    this.apiModel.set(model);

    // FIX NOTE AUTHORS
    // converts author ID to User for each note
    const notes = [];
    const userColl = this.uiModel.getUsers();

    // iterate over the raw notes array
    _.each(model.notes, (note) => {
      // find user by id (note.author)
      const user = userColl.get(note.author);
      // update author ID with actual user object when found
      note.author = user ? user.toJSON() : null;
      // remove "checked" prop, which is not supported by note model
      delete note.author.checked;
      // push note into notes array
      notes.push(note);
    });
    // create note collection from notes array
    this.setupData.notesList = new NoteCollection(notes);

    // enable selected auxTypes
    this.setupData.auxTypes.map((attr) => {
      const checked = model.auxType === attr.getKey() ? true : false;
      attr.setChecked(checked);
    });

    // enable selected placesOfService
    this.setupData.placesOfService.map((attr) => {
      const checked = model.placeOfServiceCode === attr.getKey() ? true : false;
      attr.setChecked(checked);
    });

    // enable selected supplementalReasonCodes
    this.setupData.supplementalReasonCodes.map((attr) => {
      const checked =
        model.supplementalReasonCode === attr.getKey() ? true : false;
      attr.setChecked(checked);
    });

    // fetch dispositions and chart
    const endpointConfig = {
      id: model.id,
    };
    const dispositionsEndpoint = environment.getEndpoint(
      "chart-reviews:drg-deletes-dispositions",
      endpointConfig
    );

    $.when($.ajax(dispositionsEndpoint)).then(
      // on success
      (dispositions) => {
        this.setupData.dispositions = new StringKeyValueCollection(
          dispositions
        );

        // update disposition label
        this.setupData.dispositionLabel = model.disposition;
        try {
          this.setupData.dispositionLabel = this.setupData.dispositions
            .findWhere({
              key: model.disposition,
            })
            .getValue();
        } catch (e) {
          console.warn(
            `Unable to translate current disposition, ${model.disposition}`
          );
        }

        // enable selected disposition
        this.setupData.dispositions.map((attr) => {
          const checked = model.disposition === attr.getKey() ? true : false;
          attr.setChecked(checked);
        });

        // fetch member eligibility and HCC expected categories
        const memberId = this.uiModel.get("member").get("id");
        $.when(
          $.ajax(
            environment.getEndpoint("member:eligibility", { id: memberId })
          ),
          $.ajax({
            url: environment.getEndpoint("member:expected-categories", {
              id: memberId,
            }),
            data: {
              benefitYear: ["processingYear", "processingYear"],
              asOfMonth: "latest",
              length: 100,
              start: 0,
            },
          })
        ).then(
          // on success
          (eligibility, categories) => {
            this.setupData.memberCategories = createMemberCategories(
              categories[0]
            );

            // attach instance of eligibility collection to member model
            this.uiModel
              .get("member")
              .setEligibility(new MemberEligibilityCollection(eligibility[0]));

            // update chart model with updated member and project name
            this.chartModel.set({
              selectedMember: this.uiModel.get("member"),
              projectName: this.uiModel.get("projectName"),
            });

            // update tab data with API and chart models
            this.collection.each((tab) => {
              const data = tab.getData();
              data.apiModel = this.apiModel;
              data.chartModel = this.chartModel;
              tab.setData(data);
            });

            // update drg model with setup data
            this.uiModel.set(this.setupData);

            // finally, inject views
            this._renderRegions();
          },
          // on failure
          () => {
            // close this modal
            this._hide();

            // show error
            channel.trigger("status:show", {
              autoDismiss: false,
              type: "danger",
              message:
                "We were unable to retrieve eligibility data. Please try again later.",
            });
          }
        );
      },
      // on failure
      () => {
        // close this modal
        this._hide();

        // show error
        channel.trigger("status:show", {
          autoDismiss: false,
          type: "danger",
          message:
            "We were unable to retrieve chart data. Please try again later.",
        });
      }
    );

    // console.dir(this.uiModel.toJSON());
  }

  _onDRGModelLoadFailure() {
    // close this modal
    this._hide();

    // show error
    channel.trigger("status:show", {
      autoDismiss: false,
      type: "danger",
      message:
        "We were unable to retrieve the Diag Review Group details. Please try again later.",
    });
  }

  _renderRegions() {
    // add views to regions
    this.collection.each((tab) => {
      // extract view and view model from config
      const View = tab.getView();
      const ViewModel = tab.getViewModel();
      const data = tab.getData();

      // does config define a view?
      if (View) {
        // does config define a view model?
        let view;
        if (ViewModel) {
          // configure view with view model
          view = new View({
            model: new ViewModel(),
            data,
          });
        } else {
          // configure view with passed in model
          view = new View({
            model: this.uiModel,
            data,
          });
        }

        // render the view
        this[tab.getId()].show(view);
      }
    });
  }

  _onTabShown(e) {
    channel.trigger("tab-activated", $(e.target).attr("aria-controls"));
  }

  _hide() {
    // notify parent layout of this change
    this.triggerMethod("modal:close");
  }
}

export { View as default };
